import { useEffect, useState } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import { qk } from '@immoaktas/api-client';
import { FREE_UNITS, euro, fmtDate, volumeSavingsCents, yearlyPriceCents } from '@immoaktas/shared';
import { api, role } from '../../../lib/api';
import { can, useNotify, usePaywall } from '../../../lib/hooks';
import { averageCents, breakdownText, storeRenews, webActive } from '../../../lib/pricing';
import { siteUrl, webPurchaseBlock } from '../../../lib/purchase';
import { Banner, Button, Dialog, Icon, Skeleton } from '../../../ui';
import { OrderConfirmDialog } from './OrderConfirm';

/** Queries whose data depends on unit locks (computed live on the server); everything else stays cached. */
export const LOCK_KEYS: readonly (readonly unknown[])[] = [qk.billing, qk.me, qk.account, qk.properties, ['property'], ['unit'], ['tenancies'], ['tenancy'], ['periods'], ['period'], ['statement'], ['dashboard']];
const refreshLocks = (qc: QueryClient) => Promise.all(LOCK_KEYS.map((queryKey) => qc.invalidateQueries({ queryKey })));

/**
 * Einheiten freischalten (design/Paywall.dc.html), "Erst einrichten, dann freischalten" (docs/BILLING.md §5): opened by
 * the incentive banners or by a 402 UNITS_LOCKED of a gated action (details: needed, allowed, locked). Everything the
 * user entered is already saved; the dialog only offers exactly the quantity that unlocks every unit. Prices only from
 * the shared pricing config.
 * Paths (docs/BILLING.md): web subscription → units are changed right here (prorated); payment problem → Stripe portal;
 * renewing store subscription → extend it in the app; otherwise (free, expired, store cancelled) → Stripe Checkout,
 * returning to the current page.
 */
export function Paywall() {
  const { t } = useTranslation();
  const { open, details, hide } = usePaywall();
  const toast = useNotify();
  const qc = useQueryClient();
  const loc = useLocation();
  const [going, setGoing] = useState(false);
  const sum = useQuery({ queryKey: qk.billing, queryFn: api.subscription.summary, enabled: open });
  const b = sum.data;
  // back from Stripe Checkout: the webhook reaches the server a moment later, then the units unlock on their own.
  // Locks are computed live, so the lock-dependent queries (units, overviews, statements, billing) are refetched a few times.
  const back = new URLSearchParams(loc.search).get('checkout') === 'success';
  useEffect(() => {
    if (!back) return;
    const timers = [1500, 4000, 9000, 20000].map((ms) => setTimeout(() => void refreshLocks(qc), ms));
    return () => timers.forEach(clearTimeout);
  }, [back, qc]);
  const needed = Math.max(Number(details?.needed ?? b?.neededUnits ?? FREE_UNITS + 1), FREE_UNITS + 1);
  const allowed = Math.max(Number(details?.allowed ?? b?.allowedUnits ?? FREE_UNITS), 0);
  const locked = Math.max(Number(details?.locked ?? b?.lockedUnits ?? needed - allowed), 0);
  const web = webActive(b);
  const store = storeRenews(b);
  const paymentIssue = !!b?.paymentIssue;
  const admin = can(role(), 'admin');
  // no Stripe from the mobile app (Apple 3.1.1) or when Stripe is off: limits are explained, no web prices or checkout
  const block = webPurchaseBlock();
  const parts = (n: number) => breakdownText(n, { free: (count) => t('paywall.free', { count }), part: (count, price) => t('paywall.part', { count, price }), price: euro });
  // every purchase goes through the order step (§ 312j/§ 356 BGB, OrderConfirmDialog); fixing the payment does not
  const [ordering, setOrdering] = useState(false);
  const go = async () => {
    if (!(web && paymentIssue)) return setOrdering(true);
    setGoing(true);
    try {
      window.location.href = (await api.subscription.portal()).url;
    } catch (e) {
      toast.err(e);
      setGoing(false);
    }
  };
  const order = async () => {
    setGoing(true);
    try {
      if (web) {
        const r = await api.subscription.setUnits(needed, true);
        // locks are computed live: units, overviews and statements change with the quantity
        await refreshLocks(qc);
        toast.ok(r.pending ? t('paywall.pending') : t('paywall.extended', { count: r.activeUnits }));
        setGoing(false);
        setOrdering(false);
        hide();
        return;
      }
      window.location.href = (await api.subscription.checkout(needed, loc.pathname, true)).url;
    } catch (e) {
      toast.err(e);
      setGoing(false);
    }
  };
  const max = Math.max(needed, allowed, 1);
  return (
    <Dialog open={open} onClose={hide} sectioned title={t('paywall.titleUnlock')}
      footNote={block === 'app' ? undefined : <Link to="/einstellungen/abo" onClick={hide} className="text-link">{t('paywall.details')}</Link>}
      actions={<>
        <Button variant="secondary" onClick={hide}>{t('paywall.later')}</Button>
        {admin && !store && !block && <Button loading={going} disabled={!b} onClick={() => void go()}>{web && paymentIssue ? t('paywall.fixPayment') : t('paywall.unlockAll', { count: needed })}</Button>}
      </>}>
      <div className="dialog-pad stack paywall">
        {!b ? <Skeleton count={4} h={22} /> : (
          <>
            <p className="paywall-lead"><Trans i18nKey="paywall.leadUnlock" count={locked} values={{ allowed, needed, locked }} components={{ strong: <strong /> }} /></p>
            {paymentIssue && <Banner tone="err" icon={<Icon name="alert" size={18} strokeWidth={2} />}>{web ? t('paywall.paymentIssue') : t('paywall.paymentIssueStore')}</Banner>}
            <div className="usage-bars" role="group" aria-label={t('paywall.usage')}>
              <span>{t('paywall.today')}</span><span className="usage-track"><span className="usage-fill now" style={{ width: `${Math.max(3, (allowed / max) * 100)}%` }} /></span><strong>{allowed}</strong>
              <span>{t('paywall.withChange')}</span><span className="usage-track"><span className="usage-fill old" style={{ width: `${Math.max(3, (allowed / max) * 100)}%` }} /><span className="usage-fill add" style={{ width: `${Math.max(0, ((needed - allowed) / max) * 100)}%` }} /></span><strong>{needed}</strong>
            </div>
            {block && <Banner tone="info" icon={<Icon name="info" size={18} />}>{block === 'app' ? t('purchase.inApp') : t('purchase.webOff')}</Banner>}
            {block !== 'app' && <>
            <div className="price-compare">
              <div className="price-card">
                <span className="pc-label">{t('paywall.now', { count: allowed })}</span>
                <strong className="pc-price">{euro(yearlyPriceCents(allowed))}</strong>
                <span className="pc-sub">{t('paywall.perYear', { parts: parts(allowed) || t('paywall.free', { count: 0 }) })}</span>
              </div>
              <div className="price-card new">
                <span className="pc-label">{t('paywall.new', { count: needed })}</span>
                <strong className="pc-price">{euro(yearlyPriceCents(needed))}</strong>
                <span className="pc-sub">{t('paywall.perYear', { parts: parts(needed) })}</span>
                <span className="pc-avg">{t('paywall.average', { amount: euro(averageCents(needed)) })}</span>
                {volumeSavingsCents(needed) > 0 && <span className="pc-avg">{t('paywall.savings', { amount: euro(volumeSavingsCents(needed)) })}</span>}
              </div>
            </div>
            <div className="paywall-due">
              <span>{web && b.stripe?.periodEnd ? t('paywall.prorated', { date: fmtDate(b.stripe.periodEnd) }) : t('paywall.checkoutNote')}</span>
            </div>
            </>}
            {store && <Banner tone="warn" icon={<Icon name="alert" size={18} strokeWidth={2} />}>{t('paywall.store')}</Banner>}
            {!admin && <Banner tone="lock" icon={<Icon name="lock" size={18} />}>{t('paywall.ownerOnly')}</Banner>}
            <Banner tone="ok" icon={<Icon name="check" size={18} strokeWidth={2} />}><Trans i18nKey="paywall.safe" components={{ strong: <strong /> }} /></Banner>
            {block !== 'app' && <p className="small muted">{t('order.vat')}</p>}
            <p className="small muted"><Trans i18nKey="paywall.terms" components={{ terms: <a href={siteUrl("https://immoaktas.com/agb")} target="_blank" rel="noreferrer" />, privacy: <a href={siteUrl("https://immoaktas.com/datenschutz")} target="_blank" rel="noreferrer" /> }} /></p>
          </>
        )}
      </div>
      {!block && (
        <OrderConfirmDialog open={ordering} onClose={() => setOrdering(false)} onOrder={() => void order()} loading={going}
          product={t('order.productUnits', { count: needed })} yearlyCents={yearlyPriceCents(needed)}
          preview={web ? { kind: 'units', units: needed } : undefined} checkout={!web}
          note={web && b?.stripe?.periodEnd ? t('paywall.prorated', { date: fmtDate(b.stripe.periodEnd) }) : undefined} />
      )}
    </Dialog>
  );
}
